import type { MyFilesBreadcrumb, MyFilesResponse } from "@nlc/shared-types";
import type { RequestTypeEnum } from "@nlc/shared-types";

import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { useIsMyFileTemplate } from "@/features/my-files/hooks/useIsMyFileTemplate.tsx";
import authenticatedApi from "@/lib/api/client";

import type { MyFilesSearchSchema } from "../page-search-schema";
import type { FileItem } from "../types";

import { getFileConfig } from "../get-file-config";
import { getMyFilesStructureTemplateData } from "./useGetMyFilesStructureTemplate";

function transformFileStructureToFileItem(
  fileStructure: MyFilesResponse["structure"]["children"],
): FileItem[] {
  if (!fileStructure) return [];

  return fileStructure.map((item) => {
    const isFolder = item.isDirectory;

    return isFolder
      ? {
          createdAt: "-",
          href: item.slug,
          id: item.id,
          isDirectory: item.isDirectory,
          name: item.label,
          size: 0,
          type: "folder",
          version: "-",
        }
      : {
          createdAt: dayjs(item.createdAt).format("MMM DD, YYYY"),
          extension: item.extension,
          id: item.id,
          isDirectory: item.isDirectory,
          name: `${item.label} - ${item.designerInitials}`,
          size: Number(item.size),
          type: "file",
          url: item.url || "",
          version: item.version,
        };
  });
}

export function useGetMyFilesStructure(params: MyFilesSearchSchema) {
  const configUrl = getFileConfig(params);
  const isTemplate = useIsMyFileTemplate();

  return useQuery<
    { breadcrumbs: MyFilesBreadcrumb[]; files: FileItem[] },
    Error,
    { breadcrumbs: MyFilesBreadcrumb[]; files: FileItem[] }
  >({
    enabled: !!configUrl,
    queryFn: async () => {
      if (isTemplate) {
        const templateData = getMyFilesStructureTemplateData(
          params.requestType as RequestTypeEnum.GRAPHIC | RequestTypeEnum.VIDEO,
        );

        return {
          breadcrumbs: templateData.breadcrumbs,
          files: transformFileStructureToFileItem(
            templateData.structure?.children || [],
          ),
        };
      }

      if (!configUrl) return { breadcrumbs: [], files: [] };

      try {
        const response = await authenticatedApi
          .url(`/my-files${configUrl}`)
          .get()
          .json<MyFilesResponse>();

        return {
          breadcrumbs: response?.breadcrumbs || [],
          files: transformFileStructureToFileItem(
            response?.structure?.children || [],
          ),
        };
      } catch (error) {
        // For 403 errors, we want to return a special error object
        // that we can check for in the component
        if (
          error &&
          typeof error === "object" &&
          "statusCode" in error &&
          error.statusCode === 403
        ) {
          throw Object.assign(new Error("Forbidden"), { statusCode: 403 });
        }
        throw error;
      }
    },
    queryKey: ["myFilesStructure", configUrl],
    refetchOnWindowFocus: false,
    retry: false,
  });
}
